
import React, { useState } from 'react';
import { UserRole, Meeting, UserProfile } from '../types';

interface NavbarProps {
  onNavigate: (view: 'dashboard' | 'profile' | 'create_community' | 'landing' | 'admin') => void;
  activeView: string;
  isLoggedIn: boolean;
  userRole?: UserRole;
  onLogin: () => void;
  onLogout: () => void;
  pendingInvites?: Meeting[];
  users: UserProfile[];
  onActionMeeting: (meetingId: string, action: 'scheduled' | 'declined', reason?: string) => void;
  botConnected?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({ 
  onNavigate, activeView, isLoggedIn, userRole, onLogin, onLogout, pendingInvites = [], users, onActionMeeting, botConnected 
}) => {
  const [showNotif, setShowNotif] = useState(false);

  return (
    <nav className="bg-white border-b border-gray-100 sticky top-0 z-50 py-1">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex items-center space-x-2 cursor-pointer group shrink-0" onClick={() => onNavigate(isLoggedIn ? 'dashboard' : 'landing')}>
            <div className="w-9 h-9 bg-black rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-white font-black text-lg">V</span>
            </div>
            <div className="hidden xs:block">
              <span className="font-black text-sm text-gray-900 tracking-tighter block leading-none">Random Coffee</span>
              <span className="text-[7px] font-black text-red-600 uppercase tracking-widest">Vietnam</span>
            </div>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            {isLoggedIn ? (
              <>
                <div className="relative">
                  <button onClick={() => setShowNotif(!showNotif)} className={`p-2 rounded-xl transition-all relative ${pendingInvites.length > 0 ? 'text-red-600 bg-red-50' : 'text-gray-400'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                    {pendingInvites.length > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-red-600 border border-white rounded-full"></span>}
                  </button>

                  {showNotif && (
                    <div className="fixed inset-x-4 md:absolute md:inset-x-auto md:right-0 mt-4 md:w-80 bg-white border border-gray-100 rounded-3xl shadow-2xl p-6 animate-slideDown max-h-[70vh] overflow-y-auto z-[60]">
                      <div className="flex justify-between items-center mb-6">
                        <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400">Уведомления</h4>
                        <button onClick={() => setShowNotif(false)} className="md:hidden text-gray-400">✕</button>
                      </div>
                      {pendingInvites.length === 0 ? <p className="text-center text-xs text-gray-400 py-4 italic">Нет новых приглашений</p> : (
                        <div className="space-y-6">
                          {pendingInvites.map(m => {
                            const inviter = users.find(u => u.id === m.inviterId);
                            return (
                              <div key={m.id} className="border-b border-gray-50 pb-4">
                                <div className="flex items-center gap-3 mb-2">
                                  <img src={inviter?.photo} className="w-8 h-8 rounded-lg object-cover" />
                                  <div>
                                    <p className="text-xs font-black">{inviter?.name}</p>
                                    <p className="text-[9px] text-gray-500 font-bold">{m.meeting_date} в {m.meeting_time}</p>
                                  </div>
                                </div>
                                <div className="bg-gray-50 p-3 rounded-xl mb-3">
                                  <p className="text-[9px] text-gray-400 font-black uppercase mb-1">Место: {m.location}</p>
                                  <p className="text-[10px] text-gray-600 italic leading-relaxed">"{m.message}"</p>
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => onActionMeeting(m.id, 'scheduled')} className="flex-1 py-2 bg-black text-white text-[8px] font-black uppercase rounded-lg">Принять</button>
                                  <button onClick={() => {
                                    const reason = prompt('Добавьте комментарий или укажите причину отказа:');
                                    if(reason !== null) onActionMeeting(m.id, 'declined', reason);
                                  }} className="flex-1 py-2 border border-gray-100 text-gray-400 text-[8px] font-black uppercase rounded-lg">Ответ</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {userRole === 'service_admin' && (
                  <button onClick={() => onNavigate('admin')} className={`p-2 rounded-xl transition-all ${activeView === 'admin' ? 'text-black bg-gray-100' : 'text-gray-400'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                  </button>
                )}
                <button onClick={() => onNavigate('dashboard')} className={`p-2 rounded-xl transition-all ${activeView === 'dashboard' ? 'text-black bg-gray-100' : 'text-gray-400'}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
                </button>
                <div className="relative">
                  <button onClick={() => onNavigate('profile')} className={`p-2 rounded-xl transition-all ${activeView === 'profile' ? 'text-black bg-gray-100' : 'text-gray-400'}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  </button>
                  {!botConnected && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></span>}
                </div>
                <button onClick={onLogout} className="p-2 text-gray-400 hover:text-red-600 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                </button>
              </>
            ) : (
              <button onClick={onLogin} className="px-6 py-2.5 bg-black text-white text-[10px] font-black uppercase tracking-widest rounded-xl">Войти</button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
